<template>
  <v-row no-gutters>
    <v-col :cols="newSession.periods.length ? 6 : 3">
      <DatePicker v-model="newSession.date" title="Date de la session *" :readonly="!canChangeDate" icon="mdi-clock" />
    </v-col>
    <v-col v-if="canShowSessionTimeField" cols="3">
      <v-list-item>
        <v-list-item-content>
          <v-list-item-subtitle>Début *</v-list-item-subtitle>
          <v-list-item-title>
            <v-select
              v-model="startTime"
              dense
              placeholder="Début *"
              :rules="mandatoryRule"
              :items="computedStartTime"
              item-value="value"
              item-text="label"
              chips
              persistent
              clearable
              :menu-props="{ 'z-index': '1500 !important'}"
              required
              color="black"
              @input="changeSessionTime('start_time', startTime)"
            />
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </v-col>
    <v-col v-if="canShowSessionTimeField && canShowSessionEndTimeField" cols="3">
      <v-list-item>
        <v-list-item-content>
          <v-list-item-subtitle>Fin *</v-list-item-subtitle>
          <v-list-item-title>
            <v-select
              v-model="endTime"
              dense
              placeholder="Fin *"
              :rules="mandatoryRule"
              :items="computedEndTime"
              item-value="value"
              item-text="label"
              chips
              persistent
              clearable
              :menu-props="{ 'z-index': '1500 !important'}"
              required
              color="black"
              @input="changeSessionTime('end_time', endTime)"
            />
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </v-col>
    <v-col v-if="canShowPeriods" class="d-flex align-center justify-end text-subtitle-1" :cols="newSession.periods.length ? 6 : 3">
      <Button
        icon="mdi-plus"
        class="d-flex align-center font-weight-bold d-flex justify-center font-weight-bold full-height"
        :outlined="true"
        label="Ajouter une période"
        :remove-margin="true"
        color="third"
        :large="true"
        :icon-color="null"
        :plain="true"
        :action="addNewTime"
        :disabled="editable"
      />
    </v-col>
    <SessionPeriods v-if="canShowPeriods" />
    <v-col cols="3">
      <DatePicker v-if="newSession.allDay" v-model="newSession.end_date" :select-end-date="!showSessionEndDate" title="Fin de la session *" />
    </v-col>
  </v-row>
</template>

<script>
import { computed, onBeforeMount, inject, watch, ref } from '@vue/composition-api';
import { fromUnixTime, getUnixTime, format, add } from 'date-fns';
import { canShowFields, mandatoryRule, canShowFieldsBasedOnSessionType } from '@/helpers/utils';
import { startTimeOptions, endTimeOptions } from '@/helpers/dataManager';
import { addPeriodToSessionPeriodArray, longSessionTypes, sessionsWithPeriods, golfSession } from '@/helpers/sessions';

import DatePicker from '@/components/common/Form/DatePicker.vue';
import Button from '@/components/common/Button.vue';
import SessionPeriods from './SessionPeriods.vue';

import store from '@/store';

export default {
  components: { DatePicker, Button, SessionPeriods },
  props: {
    selectSessionDate: {
      type: Boolean,
      default: false
    }
  },
  setup (props) {
    const newSession = inject('newSession');
    const sessionReplica = inject('sessionReplica');
    const editable = inject('editable', ref(false));

    const startTime = ref(null);
    const endTime = ref(null);

    const selectedDate = computed(() => store.getters.selectedDate);

    const canChangeDate = computed(() => {
      if(props.selectSessionDate && !editable.value) {
        return true;
      }
      return false;
    });

    const canShowPeriods = computed(() => canShowFieldsBasedOnSessionType(newSession.value.type, sessionsWithPeriods));

    const updateSelectedTime = () => {
      startTime.value = format(new Date(fromUnixTime(newSession.value.start_time)), 'HH:mm');
      endTime.value = format(add(new Date(fromUnixTime(newSession.value.end_time)), { seconds: 1 }), 'HH:mm');
    };

    const computedStartTime = computed(() => startTimeOptions(newSession.value.type));
    const computedEndTime = computed(() => endTimeOptions());

    watch(() => newSession.value, () => {
      updateSelectedTime();
    });

    onBeforeMount(() => {
      updateSelectedTime();
    });

    const addNewTime = () => {
      newSession.value.periods = addPeriodToSessionPeriodArray(newSession.value);
    };

    const canShowSessionTimeField = computed(() => {
      if (sessionsWithPeriods.includes(newSession.value.type)) {
        if (!editable.value && (newSession.value.periods || []).length) {
          return false;
        }
        return true;
      }
      if (sessionReplica.value.isReplica && !newSession.value.allDay) {
        return true;
      }

      if (newSession.value.allDay) {
        return false;
      }

      return true;
    });

    const isGolfSession = computed(() => golfSession(newSession.value.type));

    const canShowSessionEndTimeField = computed(() => {
      if (isGolfSession.value) {
        return false;
      }
      return true;
    });

    const changeSessionTime = (sessionField, value) => {
      let date = props.selectSessionDate ? new Date(`${format(fromUnixTime(newSession.value.date), 'yyyy-MM-dd')}T${value}`) : new Date(`${selectedDate.value}T${value}`);
      if (value === '00:00') {
        date = add(date, { days: 1 });
      }
      newSession.value[sessionField] = getUnixTime(date);

      if(sessionField === 'start_time' && isGolfSession.value) {
        newSession.value.end_time = getUnixTime(add(date, { minutes: 10 }));
      }
    };

    watch(() => newSession.value.type, () => {
      if(isGolfSession.value) {
        newSession.value.end_time = getUnixTime(add(new Date(fromUnixTime(newSession.value.start_time)), { minutes: 10 }));
      }
    });

    watch(() => newSession.value.end_date, () => {
      sessionReplica.value.replica_end_date = format(fromUnixTime(newSession.value.end_date), 'yyyy-MM-dd');
    });

    const showSessionEndDate = computed(() => editable.value && !!newSession.value.allDay && longSessionTypes.includes(newSession.value.type));

    return { showSessionEndDate, sessionsWithPeriods, editable, changeSessionTime, startTime, endTime, computedStartTime, computedEndTime, canShowFieldsBasedOnSessionType, addNewTime, canShowSessionTimeField, newSession, canShowFields, mandatoryRule, sessionReplica, canChangeDate, canShowPeriods, canShowSessionEndTimeField };
  }
};
</script>
